import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ImportGroupSelector } from "@/components/admin/ImportGroupSelector";
import { ImportLog } from "@/components/admin/ImportLog";
import { ImportProjectSelector } from "@/components/admin/ImportProjectSelector";
import { CommentImportPreviewTable } from "@/components/admin/CommentImportPreviewTable";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  useMondayCommentsImport,
  type CommentImportResponse,
  type CommentImportOverride,
} from "@/hooks/use-monday-comments-import";
import { useRehostChatAssets } from "@/hooks/use-rehost-chat-assets";
import { PROJECT_BOARDS } from "@/lib/monday-boards";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * Manuell import av Monday-prosjektoppdateringer (kommentarer, vedlegg,
 * likes, replies). Velg prosjekt-board, grupper, kjør dry-run, juster
 * overrides for items uten automatisk match, bekreft.
 */
export function CommentsImportTab() {
  const { activeUser } = useImpersonation();
  const importMut = useMondayCommentsImport();
  const rehostMut = useRehostChatAssets();

  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);
  const [customBoardInput, setCustomBoardInput] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedProjectCodes, setSelectedProjectCodes] = useState<string[]>([]);
  const [lastResult, setLastResult] = useState<CommentImportResponse | null>(null);
  const [overrides, setOverrides] = useState<Record<string, CommentImportOverride>>(
    {},
  );
  const [running, setRunning] = useState(false);

  const resetSelection = () => {
    setSelectedGroupIds([]);
    setLastResult(null);
    setOverrides({});
  };

  const handleApplyCustomBoard = () => {
    const id = Number(customBoardInput.trim());
    if (!id || isNaN(id)) return;
    setSelectedBoardId(id);
    resetSelection();
  };

  const handleRun = async (dryRun: boolean) => {
    if (!selectedBoardId || selectedGroupIds.length === 0) return;
    setRunning(true);
    try {
      const res = await importMut.mutateAsync({
        boardId: selectedBoardId,
        groupIds: selectedGroupIds,
        dryRun,
        triggeredBy: activeUser?.id ?? null,
        overrides: dryRun ? undefined : overrides,
        projectCodes:
          selectedProjectCodes.length > 0 ? selectedProjectCodes : undefined,
      });
      setLastResult(res);
      if (!dryRun) {
        setOverrides({});
        // Kjør re-hosting av chat-bilder automatisk rett etter en ekte import,
        // så bildene flyttes fra (utløpende) Monday-URLer til Storage i samme
        // operasjon — admin slipper å kjøre en separat prosess. Re-hosting har
        // egen toast/feilhåndtering i hooken; importen regnes som vellykket
        // uansett om re-hosting feiler.
        await rehostMut.mutateAsync().catch(() => {});
      }
    } catch {
      /* toast håndteres i hook */
    } finally {
      setRunning(false);
    }
  };

  const handleOverrideChange = (key: string, patch: CommentImportOverride) => {
    setOverrides((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Konfigurer import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Prosjekt-board
            </label>
            <div className="flex flex-wrap gap-2">
              {PROJECT_BOARDS.map((b) => {
                const sel = selectedBoardId === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => {
                      setSelectedBoardId(b.id);
                      resetSelection();
                    }}
                    className={cn(
                      "flex flex-col items-start gap-0.5 px-3 py-2 rounded border text-left text-sm hover:bg-muted/40 transition-colors",
                      sel ? "border-primary bg-primary/10" : "border-foreground/30",
                    )}
                  >
                    <span className="font-medium">{b.label}</span>
                    {b.description && (
                      <span className="text-[10px] text-muted-foreground">
                        {b.description}
                      </span>
                    )}
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      ID {b.id}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                type="text"
                value={customBoardInput}
                onChange={(e) => setCustomBoardInput(e.target.value)}
                placeholder="Annet board-ID..."
                className="px-2 py-1 text-xs border rounded w-[180px] bg-background"
              />
              <button
                type="button"
                onClick={handleApplyCustomBoard}
                disabled={!customBoardInput.trim()}
                className="text-[11px] text-muted-foreground hover:text-primary disabled:opacity-40"
              >
                Bruk annet board
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Grupper
            </label>
            <ImportGroupSelector
              boardId={selectedBoardId}
              selectedGroupIds={selectedGroupIds}
              onChange={(ids) => {
                setSelectedGroupIds(ids);
                setLastResult(null);
                setOverrides({});
              }}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Filtrér til prosjekter (valgfritt)
            </label>
            <p className="text-[11px] text-muted-foreground">
              Tom = importér alle prosjekter i valgte grupper.
            </p>
            <ImportProjectSelector
              selectedCodes={selectedProjectCodes}
              onChange={(codes) => {
                setSelectedProjectCodes(codes);
                setLastResult(null);
                setOverrides({});
              }}
            />
          </div>

          <div className="flex items-center gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => handleRun(true)}
              disabled={
                running ||
                !selectedBoardId ||
                selectedGroupIds.length === 0
              }
            >
              {running ? (
                <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <SrIcon name="eye" className="w-4 h-4 mr-1.5" />
              )}
              Dry-run
            </Button>
            <Button
              onClick={() => handleRun(false)}
              disabled={
                running ||
                !selectedBoardId ||
                selectedGroupIds.length === 0 ||
                !lastResult ||
                lastResult.stats?.totalItems === 0
              }
              title={!lastResult ? "Kjør dry-run først" : undefined}
            >
              {running ? (
                <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <SrIcon name="check" className="w-4 h-4 mr-1.5" />
              )}
              Bekreft import
            </Button>
            {lastResult?.stats && (
              <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                {lastResult.stats.totalItems} items —{" "}
                {lastResult.stats.matched} match,{" "}
                {lastResult.stats.skipped} skip
                {lastResult.stats.commentsUpserted > 0 &&
                  ` · ${lastResult.stats.commentsUpserted} kommentarer importert`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {lastResult?.preview && lastResult.preview.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Forhåndsvisning og redigering</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Velg prosjekt manuelt for items uten match. Slå av enkeltlinjer
              med skip-toggle.
            </p>
          </CardHeader>
          <CardContent>
            <CommentImportPreviewTable
              preview={lastResult.preview}
              overrides={overrides}
              onOverrideChange={handleOverrideChange}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Re-host chat-bilder</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Monday lagrer chatbilder bak beskyttede lenker som ikke vises i Sølvrev.
            Denne henter dem ned og lagrer dem permanent, en bunke om gangen.
            Trygt å kjøre flere ganger — den hopper over bilder som allerede er hentet.
          </p>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={() => rehostMut.mutate()}
            disabled={rehostMut.isPending}
          >
            {rehostMut.isPending ? (
              <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <SrIcon name="download" className="w-4 h-4 mr-1.5" />
            )}
            Re-host chat-bilder
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Import-logg</CardTitle>
        </CardHeader>
        <CardContent>
          <ImportLog entityType="comments" />
        </CardContent>
      </Card>
    </div>
  );
}
